import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { mainNavigation } from '../config/navigation';
import ThemeToggle from './ThemeToggle';
import Logo from './Logo';
import AnimatedNavItem from './animations/AnimatedNavItem';
import AnimatedMenuButton from './animations/AnimatedMenuButton';
import AnimatedBackdrop from './animations/AnimatedBackdrop';
import AnimatedMobileDrawer from './animations/AnimatedMobileDrawer';
import './Navbar.css';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();


  const toggleMenu = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      // Focus trap for mobile menu
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  };

  const closeMenu = () => {
    setIsOpen(false);
    document.body.style.overflow = '';
  };

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const isItemActive = (item: any) => {
    if (isActive(item.path)) return true;
    return false;
  };


  // Close mobile menu on route change
  useEffect(() => {
    closeMenu();
  }, [location.pathname]);

  return (
    <>
      {/* Skip to content link for accessibility */}
      <a 
        href="#main-content" 
        className="skip-link"
      >
        Skip to main content
      </a>
      
      {/* Premium Header with Blur Effect */}
      <header 
        className="sticky top-0 z-50 transition-all duration-300"
        style={{
          background: 'var(--color-header-bg)',
          borderBottom: '1px solid var(--color-header-border)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          boxShadow: '0 1px 3px var(--color-shadow)',
          paddingTop: 'env(safe-area-inset-top)'
        }}
      >
        <nav className="container-wrapper" role="navigation" aria-label="Main navigation">
          <div className="flex justify-between items-center" style={{ height: '56px' }}>
            {/* Logo */}
            <div className="flex items-center">
              <Link 
                to="/" 
                className="focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 rounded transition-all duration-200"
                aria-label="Open Hope Initiative - Home"
              >
                <Logo size="sm" hideText={true} />
              </Link>
            </div>

            {/* Desktop Navigation */}
            <motion.div 
              className="hidden lg:flex items-center space-x-0.5"
              initial="hidden"
              animate="visible"
              variants={{
                hidden: {},
                visible: {
                  transition: {
                    staggerChildren: 0.1,
                    delayChildren: 0.2
                  }
                }
              }}
            >
              {mainNavigation.map((item, index) => (
                <AnimatedNavItem
                  key={item.path}
                  to={item.path}
                  isActive={isItemActive(item)}
                  variant="desktop"
                  index={index}
                >
                  {item.label}
                </AnimatedNavItem>
              ))}
            </motion.div>

            {/* Right Side Controls */}
            <div className="flex items-center space-x-3">

              {/* Theme Toggle */}
              <ThemeToggle />
              
              {/* Mobile menu button */}
              <div className="lg:hidden">
                <AnimatedMenuButton
                  isOpen={isOpen}
                  onClick={toggleMenu}
                />
              </div>
            </div>
          </div>

        </nav>
      </header>
      
      {/* Animated Mobile Components */}
      <AnimatedBackdrop isVisible={isOpen} onClick={closeMenu} />
      <AnimatedMobileDrawer isOpen={isOpen} onClose={closeMenu} />
    </>
  );
};

export default Navbar;